import React, { useState, useEffect, useMemo } from "react";
import Card from "../cards/Card";
import { useCMTagList } from "api/api";
import TickValue from "components/basic/TickValue";
import { stdTimeOptions, weekTimeOption, yearTimeOption } from "utility/timeOptions";
import { Energy_Bar, TimeOption } from "model/Datatypes";
import { useGlobalState } from "AppStore";
import Icon from "resources/icons/Icon";

type Props = {
  tags: Energy_Bar["tags"];
  title?: string;
};

const EnergyBar = ({ tags, title }: Props) => {
  const { site } = useGlobalState();
  useEffect(() => {
    if (site === "food_folks") setTime(yearTimeOption);
  }, [site]);

  const [time, setTime] = useState<TimeOption>(weekTimeOption);

  const [cardState] = useState<"total" | "scaled">("total");

  const { energyData, loading } = useEnergyTotals(tags, time, site === "food_folks");

  let totalUsed =
    cardState === "total"
      ? energyData.electricityTotals
      : energyData.electricityTotals / 33000;

  let totalDelivered =
    cardState === "total" ? energyData.totalDeliverd : energyData.totalDeliverd / 33000;

  if (site === "food_folks") {
    totalUsed = 16.3;
    totalDelivered = 142.3;
  }

  let electricityTotalRatio = totalUsed / totalDelivered;

  let displayTotalUsed = {
    value: totalUsed > 10 ? totalUsed : totalUsed * 1000,
    unit: totalUsed > 10 ? "MWh" : "kWh",
  };
  let displayTotalDelivered = {
    value: totalDelivered > 10 ? totalDelivered : totalDelivered * 1000,
    unit: totalDelivered > 10 ? "MWh" : "kWh",
  };

  const coolingPercentage = (energyData.coolingTotal / energyData.totalDeliverd) * 100;
  const heatingPercentage = (energyData.heatingTotal / energyData.totalDeliverd) * 100;
  const hotWaterPercentage = (energyData.hotWaterTotal / energyData.totalDeliverd) * 100;

  // const coolingPercentage = 50;
  // const heatingPercentage = 30;
  // const hotWaterPercentage = 20;

  return (
    <Card
      flipdropdown={true}
      timeOptions={stdTimeOptions}
      onNewTimeSelected={(newTime) => {
        setTime(newTime);
      }}
      timeSelected={time}
      loading={loading}
      size="full"
    >
      <div className="w-full h-full relative flex flex-col items-center px-4 pt-4 text-xs 2xl:text-3xl">
        <div className="text-gray-600 mt-2 2xl:mt-8 ml-8 mb-2 font-medium w-full">Energy</div>
        <div className="w-full h-full flex-grow pt-6 pb-2 flex justify-center">
          <div className="w-44 2xl:w-80 mx-2 h-full flex flex-col items-center">
            <div className="flex-grow h-full w-full bg-gray-200 flex items-end">
              <div
                className="w-full h-full bg-gray-500 relative"
                style={{
                  height: `${electricityTotalRatio * 100}%`,
                }}
              >
                <div
                  className={`w-full h-full flex items-center justify-center 
                  ${
                    electricityTotalRatio > 0.4
                      ? "text-white h-full"
                      : "absolute top-0 left-0 h-6 text-gray-600"
                  }`}
                >
                  <TickValue value={displayTotalUsed.value} toFixed={0} />
                  {` ${displayTotalUsed.unit}${cardState === "scaled" ? "/m2" : ""}`}
                </div>
              </div>
            </div>
            <div className="text-center mt-1">Used</div>
          </div>

          <div className="mx-2 w-44 2xl:w-80 h-full flex flex-col">
            <div className="flex h-full">
              <div className="w-full h-full flex flex-col items-center justify-center text-white relative">
                <div className="flex bg-cyan h-full w-full items-center justify-center">
                  <TickValue value={displayTotalDelivered.value} toFixed={0} />
                  {` ${displayTotalDelivered.unit}${cardState === "scaled" ? "/m2" : ""}`}
                </div>
              </div>

              <div className="flex flex-col h-full w-32 top-0 right-0">
                <div
                  className="bg-blue-500 w-full h-full flex items-center justify-center"
                  style={{
                    height: `${coolingPercentage}%`,
                  }}
                >
                  {coolingPercentage >= 20 && (
                    <Icon type="cooling" stroke="#fff" className=" 2xl:scale-150 transform" />
                  )}
                </div>
                <div
                  className="bg-red-500 w-full h-full flex items-center justify-center"
                  style={{
                    height: `${heatingPercentage}%`,
                  }}
                >
                  {heatingPercentage >= 20 && (
                    <Icon type="heating" stroke="#fff" className=" 2xl:scale-150 transform" />
                  )}
                </div>
                <div
                  className="bg-red-300 w-full h-full flex items-center justify-center"
                  style={{
                    height: `${hotWaterPercentage}%`,
                  }}
                >
                  {hotWaterPercentage >= 20 && (
                    <Icon type="hot-water" className=" 2xl:scale-150 transform" />
                  )}
                </div>
              </div>
            </div>

            <div className="text-center mt-1">Delivered</div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default EnergyBar;

const useEnergyTotals = (tags: Energy_Bar["tags"], time: TimeOption, disabled?: boolean) => {
  const energyTags = useMemo(
    () => [
      //EMA0X_COP_CONSUMED_ENERGY_HOUR_PV //EMA0X_COP_PRODUCED_ENERGY_HOUR_PV
      tags.heating, //"Heating_Total_E",
      tags.cooling, //"Cooling_Total_E",
      tags.hot_water, //"DHW_TOTAL_E",
      tags.electricity, //"CC_USED_E",
    ],
    [tags.heating, tags.cooling, tags.hot_water, tags.electricity]
  );

  const { cmData: cmEnergyData, loading } = useCMTagList(
    energyTags,
    time.from,
    time.to,
    time.groupingsType,
    true
  );
  const [energyData, setenergyData] = useState({
    heatingTotal: 0,
    coolingTotal: 0,
    hotWaterTotal: 0,
    electricityTotals: 0,
    totalDeliverd: 0,
  });

  useEffect(() => {
    const heatingVals = cmEnergyData?.find((res) => res.name === tags.heating)?.points;
    const heatingTotal = heatingVals
      ? heatingVals[heatingVals.length - 1].value - heatingVals[0].value
      : 0;
    const coolingVals = cmEnergyData?.find((res) => res.name === tags.cooling)?.points;
    const coolingTotal = coolingVals
      ? coolingVals[coolingVals.length - 1].value - coolingVals[0].value
      : 0;
    const hotWaterVals = cmEnergyData?.find((res) => res.name === tags.hot_water)?.points;
    const hotWaterTotal = hotWaterVals
      ? hotWaterVals[hotWaterVals.length - 1].value - hotWaterVals[0].value
      : 0;
    const electricityVals = cmEnergyData?.find((res) => res.name === tags.electricity)?.points;
    const electricityTotals = electricityVals
      ? electricityVals[electricityVals.length - 1].value - electricityVals[0].value
      : 0;
    const totalDeliverd = coolingTotal + heatingTotal + hotWaterTotal;

    setenergyData({
      heatingTotal,
      coolingTotal,
      hotWaterTotal,
      electricityTotals,
      totalDeliverd,
    });
  }, [cmEnergyData, tags.heating, tags.cooling, tags.hot_water, tags.electricity]);

  return { energyData, loading };
};
